import { Popover, TextInput } from '@mantine/core';
import { useDebouncedValue, useInputState } from '@mantine/hooks';
import { Icon } from '@repo/foundations';
import FlexSearch from 'flexsearch';
import { size, trim } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useLineageFlowContext } from '../../context/LineageFlowProvider/LineageFlowContext';
import {
	isLineageFlowNode,
	type LineageFlowNode,
} from '../EntityNode/EntityNode.types';
import { useStyles } from './ActionBarSearch.styles';
import ActionBarSearchResult from './ActionBarSearchResult';

const ActionBarSearch = observer(() => {
	const { classes } = useStyles();

	const [searchTerm, setSearchTerm] = useInputState('');
	const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 500);
	const [searchResults, setSearchResults] = useState<LineageFlowNode[]>([]);

	const { nodes, onNavigateToNode } = useLineageFlowContext();

	const nodeMap = useMemo(
		() => new Map(nodes.map((node) => [node.id, node])),
		[nodes]
	);

	const index = useMemo(() => {
		const idx = new FlexSearch.Index({
			preset: 'memory',
			tokenize: 'forward',
		});
		nodes.forEach((node) => {
			if (isLineageFlowNode(node)) {
				idx.add(node.id, node.data.title);
			}
		});
		return idx;
	}, [nodes]);

	useEffect(() => {
		if (debouncedSearchTerm.trim()) {
			const results = index.search(debouncedSearchTerm, {
				limit: 10,
			}) as string[];
			const matchedItems = results.map((id) =>
				nodeMap.get(id)
			) as LineageFlowNode[];
			setSearchResults(matchedItems);
		} else {
			setSearchResults([]);
		}
	}, [debouncedSearchTerm, index, nodeMap]);

	const opened = size(trim(searchTerm)) > 0 && size(searchResults) > 0;

	const handleNavigateToNode = (nodeId: string) => {
		onNavigateToNode({ nodeID: nodeId });
		setSearchTerm('');
	};

	return (
		<Popover
			classNames={{
				dropdown: classes.dropdown,
			}}
			opened={opened}
			position="bottom-start"
		>
			<Popover.Target>
				<TextInput
					classNames={{ root: classes.inputRoot, input: classes.input }}
					icon={<Icon name="search" color="icon/secondary/default" />}
					placeholder="Search lineage"
					value={searchTerm}
					onChange={setSearchTerm}
					data-testid="lineage-search-input-2673fd928"
				/>
			</Popover.Target>
			<Popover.Dropdown>
				{searchResults.map((result) => (
					<ActionBarSearchResult
						key={result.id}
						result={result}
						navigateToNode={handleNavigateToNode}
					/>
				))}
			</Popover.Dropdown>
		</Popover>
	);
});

export default ActionBarSearch;
